/**
 * Reiter *Stammdaten* für ein Gebäude (Spec 02a, „Reiter Stammdaten").
 *
 * Alle fünf Felder direkt sichtbar, kein „Weitere Angaben" — der Datensatz
 * existiert bereits. Struktur und Verhalten spiegeln `PropertyMasterData`
 * (Feature 01): Container besitzt die Query, diese Komponente den lokalen
 * Bearbeitungszustand und die Mutationen.
 */

import type { UseQueryResult } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { ArchiveControls } from '@/components/ArchiveControls'
import { ArchivedBanner } from '@/components/ArchivedBanner'
import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { MasterDataEditSheet } from '@/components/MasterDataEditSheet'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { ApiError } from '@/lib/api'
import { areaToInputValue, parseAreaInput } from '@/features/properties/area'
import type { BuildingDetail } from '@/features/properties/api'
import {
  useArchiveBuilding,
  useArchivePreview,
  useUnarchiveBuilding,
  useUpdateBuilding,
} from '@/features/properties/api'
import { FormField, FormTextareaField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { integerToInputValue, parseIntegerInput } from '@/features/properties/integer'

interface BuildingMasterDataProps {
  readonly entityId: string
  readonly canEdit: boolean
  readonly canUnarchive: boolean
  /** `ObjectHead.archived_by_id`/`archived_by_label` (Spec 02b) — kommen von
   * `routes/ObjectDetail.tsx`, nicht von `BuildingDetail` (I9). */
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly query: UseQueryResult<BuildingDetail>
}

interface FormValues {
  readonly label: string
  readonly builtYear: string
  readonly heatingType: string
  readonly totalAreaSqm: string
  readonly notes: string
}

function deriveForm(building: BuildingDetail): FormValues {
  return {
    label: building.label,
    builtYear: building.built_year === null ? '' : integerToInputValue(building.built_year),
    heatingType: building.heating_type ?? '',
    totalAreaSqm: building.total_area_sqm === null ? '' : areaToInputValue(building.total_area_sqm),
    notes: building.notes ?? '',
  }
}

function BuildingReadField({ label, value, testId }: { readonly label: string; readonly value: string | number | null; readonly testId: string }) {
  return <div><dt className="text-label text-muted">{label}</dt><dd data-testid={testId} className="text-body text-text">{value ?? '—'}</dd></div>
}

export function BuildingMasterData({
  entityId,
  canEdit,
  canUnarchive,
  archivedById,
  archivedByLabel,
  query,
}: BuildingMasterDataProps) {
  return (
    <section data-testid="building-master-data" className="px-(--page-x) py-4">
      {query.isPending && <SkeletonRows testId="building-master-data-loading" rows={5} />}

      {query.isError && (
        <ErrorState
          testId="building-master-data-error"
          retryTestId="building-master-data-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && (
        <BuildingMasterDataForm
          entityId={entityId}
          canEdit={canEdit}
          canUnarchive={canUnarchive}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          building={query.data}
        />
      )}
    </section>
  )
}

function BuildingMasterDataForm({
  entityId,
  canEdit,
  canUnarchive,
  archivedById,
  archivedByLabel,
  building,
}: {
  readonly entityId: string
  readonly canEdit: boolean
  readonly canUnarchive: boolean
  readonly archivedById: string | null
  readonly archivedByLabel: string | null
  readonly building: BuildingDetail
}) {
  const updateMutation = useUpdateBuilding()
  const archiveMutation = useArchiveBuilding()
  const unarchiveMutation = useUnarchiveBuilding()
  const archivePreviewMutation = useArchivePreview()

  const [form, setForm] = useState<FormValues>(() => deriveForm(building))
  const [initial, setInitial] = useState<FormValues>(() => deriveForm(building))
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)
  const [archiveError, setArchiveError] = useState<string | undefined>(undefined)
  const editTriggerRef = useRef<HTMLButtonElement>(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [confirmation, setConfirmation] = useState<string | null>(null)

  const [syncedKey, setSyncedKey] = useState(`${building.id}:${String(building.version)}`)
  const currentKey = `${building.id}:${String(building.version)}`
  if (currentKey !== syncedKey) {
    setSyncedKey(currentKey)
    const next = deriveForm(building)
    setForm(next)
    setInitial(next)
  }

  const isArchived = building.archived_at !== null
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial)

  function updateField(name: keyof FormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function openEditor() {
    const next = deriveForm(building)
    setForm(next)
    setInitial(next)
    setFieldError(undefined)
    setGeneralError(undefined)
    setIsEditOpen(true)
  }

  function handleEditOpenChange(open: boolean) {
    if (open) openEditor()
    else setIsEditOpen(false)
  }

  async function handleSave(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      await updateMutation.mutateAsync({
        buildingId: entityId,
        ifMatch: String(building.version),
        body: {
          label: form.label,
          built_year: parseIntegerInput(form.builtYear),
          heating_type: form.heatingType.trim() === '' ? null : form.heatingType.trim(),
          total_area_sqm: parseAreaInput(form.totalAreaSqm),
          notes: form.notes.trim() === '' ? null : form.notes.trim(),
        },
      })
      setIsEditOpen(false)
      setConfirmation('Änderungen gespeichert')
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleUnarchive() {
    setArchiveError(undefined)
    try {
      await unarchiveMutation.mutateAsync({ buildingId: entityId, ifMatch: String(building.version) })
    } catch (error) {
      setArchiveError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <dl data-testid="building-master-data-read" className="grid gap-3 sm:grid-cols-2">
          <BuildingReadField label="Bezeichnung" value={building.label} testId="building-read-label" />
          <BuildingReadField label="Baujahr" value={building.built_year} testId="building-read-built-year" />
          <BuildingReadField label="Heizungsart" value={building.heating_type} testId="building-read-heating-type" />
          <BuildingReadField label="Gesamtfläche" value={building.total_area_sqm_display} testId="building-read-total-area-sqm" />
          <BuildingReadField label="Notizen" value={building.notes} testId="building-read-notes" />
        </dl>
        {canEdit && !isArchived && (
          <button type="button" ref={editTriggerRef} data-testid="building-edit-open" onClick={openEditor} className="border-border text-text border px-3">
            Bearbeiten
          </button>
        )}
      </div>
      {isArchived ? (
        <ArchivedBanner
          kind="building"
          canUnarchive={canUnarchive}
          parentId={building.parent_id}
          archivedById={archivedById}
          archivedByLabel={archivedByLabel}
          onUnarchive={() => void handleUnarchive()}
          isUnarchiving={unarchiveMutation.isPending}
        />
      ) : (
        <ArchiveControls
          kind="building"
          entityId={entityId}
          label={building.label}
          version={building.version}
          parentId={building.parent_id}
          isArchiving={archiveMutation.isPending}
          onArchive={(ifMatch) => archiveMutation.mutateAsync({ buildingId: entityId, ifMatch })}
          onError={setArchiveError}
          archivePreview={archivePreviewMutation}
        />
      )}
      {archiveError !== undefined && <p data-testid="building-archive-error" role="alert" className="text-label text-status-overdue">{archiveError}</p>}
      <MasterDataEditSheet open={isEditOpen} onOpenChange={handleEditOpenChange} title="Gebäude bearbeiten"
        triggerRef={editTriggerRef}
        formId="building-edit-form"
        saveTestId="building-edit-save"
        saveDisabled={!isDirty || updateMutation.isPending}
        isSaving={updateMutation.isPending}
      >
    <form id="building-edit-form" onSubmit={(event) => void handleSave(event)} className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
      <FormField
        label="Bezeichnung"
        testId="building-edit-label"
        apiField="label"
        errorPrefix="building-edit"
        required
        disabled={isArchived}
        value={form.label}
        onChange={updateField('label')}
        error={fieldError}
      />
      <FormField
        label="Baujahr"
        testId="building-edit-built-year"
        apiField="built_year"
        errorPrefix="building-edit"
        inputMode="numeric"
        disabled={isArchived}
        value={form.builtYear}
        onChange={updateField('builtYear')}
        error={fieldError}
      />
      <FormField
        label="Heizungsart"
        testId="building-edit-heating-type"
        apiField="heating_type"
        errorPrefix="building-edit"
        disabled={isArchived}
        value={form.heatingType}
        onChange={updateField('heatingType')}
        error={fieldError}
      />
      <FormField
        label="Gesamtfläche (m²)"
        testId="building-edit-total-area-sqm"
        apiField="total_area_sqm"
        errorPrefix="building-edit"
        inputMode="decimal"
        disabled={isArchived}
        value={form.totalAreaSqm}
        onChange={updateField('totalAreaSqm')}
        error={fieldError}
      />
      <FormTextareaField
        label="Notizen"
        testId="building-edit-notes"
        apiField="notes"
        errorPrefix="building-edit"
        disabled={isArchived}
        value={form.notes}
        onChange={updateField('notes')}
        error={fieldError}
      />

      {generalError !== undefined && (
        <p data-testid="building-edit-error" role="alert" className="text-label text-status-overdue">
          {generalError}
        </p>
      )}

    </form>
      </MasterDataEditSheet>
      <ConfirmationStrip message={confirmation} onDismiss={() => { setConfirmation(null) }} testId="object-save-confirmation" />
    </>
  )
}
